import { KDialog, KSelect, KSwitch, KTextInput } from "@kyburz/kdesign-system";
import { makeStyles } from "@material-ui/core";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { createBillingTariff } from "../../../../apiCalls/admin/billing_tariffs";
import useSetState from "../../../../hooks/useTypedSetState";
import { setAlert } from "../../../../redux/actions/actions";
import { simStatuses, simTariffs } from "../constants";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "row",
    gap: 20,
  },
}));

interface Props {
  open: boolean;
  handleClose: (withReload: boolean) => void;
  itemsCount: number;
}

const NewBillingTariff = ({ open, handleClose, itemsCount }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const INIT_STATE = {
    enabled: true,
    license: 1,
    name: "",
    cost: 0,
    sim_tariff: null as null | string,
    sim_status: null as null | string,
    loading: false,
  };

  const [
    { enabled, license, name, cost, sim_tariff, sim_status, loading },
    setState,
  ] = useSetState({
    ...INIT_STATE,
  });

  const isSubmitDisabled =
    !name.length ||
    !license ||
    license < 0 ||
    (!cost && cost !== 0) ||
    cost < 0 ||
    loading;

  const numberToChar = (int) => {
    const code = "A".charCodeAt(0);
    return String.fromCharCode(code + int - 1);
  };

  useEffect(() => {
    if (itemsCount + 1 <= 26)
      setState({
        name: `License ${numberToChar(itemsCount + 1)}`,
        license: itemsCount + 1,
      });
    else setState({ name: "" });
  }, [itemsCount]);

  const handleChange = (e: any) =>
    setState({ [e.target.name]: e.target.value });

  const handleCheckboxChange = () => setState({ enabled: !enabled });

  const closePopup = (reload: any) => {
    handleClose(reload);
    setState({ ...INIT_STATE });
  };

  const onSubmit = () => {
    setState({ loading: true });
    createBillingTariff({
      data: {
        enabled,
        cost,
        sim_tariff,
        sim_status,
        name,
      },
      overrides: {},
      license,
    })
      .then((res: any) => {
        if (res.data) {
          closePopup(true);
        } else {
          dispatch(
            setAlert(
              res.detail.details ||
                res.detail?.errors?.map((x: any) => (
                  <div>{JSON.stringify(x)}</div>
                )),
              "error",
            ),
          );
        }
      })
      .finally(() => setState({ loading: false }));
  };

  return (
    <KDialog
      open={open}
      title="New billing tariff"
      size="sm"
      onClose={() => closePopup(false)}
      primaryCTAonClick={onSubmit}
      primaryCTAText="Create"
      primaryCTAColor="primary"
      secondaryCTAonClick={() => closePopup(false)}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={isSubmitDisabled}
      contentClassName={classes.container}
    >
      <div>
        <KTextInput
          label="Name"
          placeholder="Name"
          value={name}
          name="name"
          onChange={handleChange}
        />
        <KTextInput
          label="License"
          placeholder="License"
          value={license}
          name="license"
          onChange={handleChange}
          type="number"
        />
        <KTextInput
          label="Cost"
          placeholder="Cost"
          value={cost}
          name="cost"
          onChange={handleChange}
          type="number"
        />
      </div>
      <div>
        <KSelect
          label="SIM Tariff (for Telefonica API)"
          value={
            simTariffs.find(({ value }) => value === sim_tariff)?.label ||
            "n.a."
          }
          onSelect={(e) => setState({ sim_tariff: e?.value || null })}
          elements={simTariffs}
          name="sim_tariff"
          height={150}
          isClearable
        />
        <KSelect
          label="SIM Status (for Telefonica API)"
          value={
            simStatuses.find(({ value }) => value === sim_status)?.label ||
            "n.a."
          }
          onSelect={(e) => setState({ sim_status: e?.value || null })}
          elements={simStatuses}
          name="sim_status"
          height={75}
          isClearable
        />
        <KSwitch
          key="billing_tariff_create"
          checked={enabled}
          name="enabled"
          label="Enabled"
          onChange={handleCheckboxChange}
        />
      </div>
    </KDialog>
  );
};

export default NewBillingTariff;
